import arrowDownIcon from '@disy/cadenza-icons/arrow-down.svg';
import arrowUpIcon from '@disy/cadenza-icons/arrow-up.svg';
import { assert } from '@disy/lib/custom-error';
import { getLogger } from '@disy/lib/logging';
import type { ResponsiveTable, ToolbarConfig } from '@disy/ui.responsive-table/responsive-table';
import { icon } from '@disy/ui/icon';
import {
  type ToolbarContent,
  type ToolbarContentElement,
  type ToolbarItem,
  createSmallBorderlessIconButton,
} from '@disy/ui/toolbar';

import i18n from './responsive-table-manual-ordering.properties';

const logger = getLogger('ui/responsive-table');

export interface ToolbarManualOrderingConfig<T> {
  availabilityPredicate: (selectedItem: T) => boolean;
  /*
   * Pass the item to move and the item which will be the next item after moving has been done.
   * If you want to move the item to the end of the list, set nextItem to undefined.
   */
  orderingHandler: (itemToMove: T, nextItem?: T) => Promise<void>;
}

export type OrderDirection = 'up' | 'down';
type ClientReorderFn = (selectedIndex: number, nextIndex: number, orderDirection: OrderDirection) => void;
interface ReorderingDisabled {
  upDisabled: boolean;
  downDisabled: boolean;
}

function createManualOrderingToolbarButtons<T>(
  manualOrderingConfig: ToolbarManualOrderingConfig<T>,
  table: ResponsiveTable<T>,
  clientReorderFn: ClientReorderFn,
  reorderDisabledPredicate: (selectedIndex: number) => ReorderingDisabled,
) {
  const selectedItem = table.selectedRows[0];
  if (!selectedItem || table.selectedRows.length > 1 || !manualOrderingConfig.availabilityPredicate(selectedItem)) {
    return [];
  }

  let selectedIndex = table.rowData.findIndex((data) => data === selectedItem);
  let downButton: HTMLButtonElement;
  let upButton: HTMLButtonElement;
  let orderingInProgress: boolean = false;

  const reorder = async (nextIndex: number, orderDirection: OrderDirection) => {
    if (orderingInProgress) {
      return;
    }
    orderingInProgress = true;

    let nextItem = table.rowData[nextIndex]; // May be undefined if nextIndex does not exist.
    try {
      await manualOrderingConfig.orderingHandler(selectedItem, nextItem);
    } catch (e) {
      orderingInProgress = false;
      logger.error('Reordering item failed.\n', e);
      return;
    }
    clientReorderFn(selectedIndex, nextIndex, orderDirection);

    // Update selectedIndex to the new position after reordering has been done.
    selectedIndex += orderDirection === 'up' ? -1 : 1;

    const clickedButton = orderDirection === 'up' ? upButton : downButton;
    const otherButton = orderDirection === 'up' ? downButton : upButton;
    const canOrder = reorderDisabledPredicate(selectedIndex);
    clickedButton.disabled = orderDirection === 'up' ? canOrder.upDisabled : canOrder.downDisabled;
    otherButton.disabled = orderDirection === 'up' ? canOrder.downDisabled : canOrder.upDisabled;
    orderingInProgress = false;
  };

  const canReorder = reorderDisabledPredicate(selectedIndex);
  return [
    {
      label: i18n('orderUp'),
      icon: icon(arrowUpIcon),
      execute: () => reorder(selectedIndex - 1, 'up'),
      renderer: (item: ToolbarItem) => {
        upButton = createSmallBorderlessIconButton(item) as HTMLButtonElement;
        return upButton;
      },
      disabled: canReorder.upDisabled,
    },
    {
      label: i18n('orderDown'),
      icon: icon(arrowDownIcon),
      execute: () => reorder(selectedIndex + 2, 'down'),
      renderer: (item: ToolbarItem) => {
        downButton = createSmallBorderlessIconButton(item) as HTMLButtonElement;
        return downButton;
      },
      disabled: canReorder.downDisabled,
    },
  ];
}

export function addOrderingButtons<T>(
  toolbarConfig: ToolbarConfig,
  manualOrderingConfig: ToolbarManualOrderingConfig<T>,
  table: ResponsiveTable<T>,
  clientReorderFn: ClientReorderFn,
  reorderDisabledPredicate: (selectedIndex: number) => ReorderingDisabled,
) {
  const addButtons = (toolbarElements: ToolbarContentElement[]) => {
    toolbarElements.splice(
      0,
      0,
      ...createManualOrderingToolbarButtons(manualOrderingConfig, table, clientReorderFn, reorderDisabledPredicate),
    );
  };

  assert(typeof toolbarConfig.items === 'function', 'Manual ordering cannot be used with static content.');
  let items: ToolbarContent = () => {
    // Though it's checked already above, need to assert again in the function block.
    assert(typeof toolbarConfig.items === 'function', 'items must be of type function.');
    const tbi = toolbarConfig.items();
    addButtons(tbi);
    return tbi;
  };
  return { ...toolbarConfig, items };
}
